import { useState } from "react";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { sendContact } from "@/lib/api";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Completá todos los campos");
      return;
    }

    setSubmitting(true);
    try {
      await sendContact(form);
      setDone(true);
      toast.success("Mensaje enviado. Gracias!");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "No se pudo enviar el mensaje. Probá de nuevo."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-grain border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[hsl(var(--accent-cyan))] mb-3">
              // section_04
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight leading-none mb-6">
              Trabajemos juntos
            </h2>
            <p className="text-muted-foreground text-base md:text-lg max-w-md">
              ¿Tenés una idea? ¿Necesitás una app a medida? Mandame un mensaje y te respondo
              cuanto antes.
            </p>

            <div className="mt-8 font-mono text-xs text-muted-foreground space-y-2">
              <div>
                <span className="text-[hsl(var(--accent-green))]">→</span> respuesta en 24-48hs
              </div>
              <div>
                <span className="text-[hsl(var(--accent-green))]">→</span> proyectos web full-stack
              </div>
              <div>
                <span className="text-[hsl(var(--accent-green))]">→</span> integraciones a medida
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div
                data-testid="contact-success"
                className="glass p-10 text-center flex flex-col items-center gap-4"
              >
                <CheckCircle2
                  size={48}
                  className="text-[hsl(var(--accent-green))]"
                />
                <h3 className="font-display text-2xl font-bold">¡Mensaje recibido!</h3>
                <p className="text-muted-foreground max-w-md">
                  Gracias por escribir. Te voy a responder al email que dejaste.
                </p>
                <button
                  data-testid="contact-send-another"
                  onClick={() => setDone(false)}
                  className="font-mono text-xs uppercase tracking-[0.15em] px-4 py-2 border border-border hover:border-foreground transition-colors"
                >
                  enviar otro
                </button>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={handleSubmit}
                className="glass p-6 md:p-8 space-y-5"
              >
                <Field label="nombre" htmlFor="contact-name">
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="tu nombre"
                    autoComplete="name"
                    required
                    className="w-full bg-transparent border-b border-border focus:border-[hsl(var(--accent-green))] outline-none py-2.5 font-mono text-sm transition-colors"
                  />
                </Field>

                <Field label="email" htmlFor="contact-email">
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="tu@email.com"
                    autoComplete="email"
                    required
                    className="w-full bg-transparent border-b border-border focus:border-[hsl(var(--accent-cyan))] outline-none py-2.5 font-mono text-sm transition-colors"
                  />
                </Field>

                <Field label="mensaje" htmlFor="contact-message">
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="contame en qué pensás..."
                    rows={5}
                    required
                    className="w-full bg-transparent border-b border-border focus:border-[hsl(var(--accent-magenta))] outline-none py-2.5 font-mono text-sm transition-colors resize-none"
                  />
                </Field>

                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={submitting}
                  className="group inline-flex items-center gap-2 bg-[hsl(var(--accent-green))] text-background font-mono font-bold uppercase tracking-[0.15em] text-xs px-6 py-3 border-2 border-[hsl(var(--accent-green))] hover:bg-transparent hover:text-[hsl(var(--accent-green))] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? "enviando..." : "enviar mensaje"}
                  <Send
                    size={14}
                    className="group-hover:translate-x-0.5 transition-transform"
                  />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label
      htmlFor={htmlFor}
      className="block font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1"
    >
      {label}
    </label>
    {children}
  </div>
);

export default Contact;
